import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { userService, getActiveUniversities } from "./services/supabaseService";
import Toast from "./components/Toast";
import "./style.css";

function Login({ onLogin }) {
  const [mode, setMode] = useState("login"); // "login", "register", "forgot-password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [toast, setToast] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("testing");
  const [universities, setUniversities] = useState([]);
  const [showUniversitiesList, setShowUniversitiesList] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Testar conexão ao carregar o componente
  useEffect(() => {
    testConnections();
    loadUniversities();
  }, []);

  const loadUniversities = async () => {
    try {
      const data = await getActiveUniversities();
      setUniversities(data || []);
    } catch (error) {
    }
  };

  const testConnections = async () => {
    setConnectionStatus("testing");
    try {
      // Teste direto com uma query simples
      const { error: dbError } = await supabase.from('users').select('count', { count: 'exact', head: true }).limit(1);
      
      if (!dbError) {
        setConnectionStatus("connected");
      } else {
        setConnectionStatus("error");
        setError("❌ Problemas de conectividade com o servidor.");
      }
    } catch (error) {
      setConnectionStatus("error");
      setError("❌ Erro de conectividade: " + error.message);
    }
  };

  // Validar se o domínio do email está cadastrado
  const isValidEmail = (email) => {
    if (!email.includes("@")) return false;
    
    const domain = email.split('@')[1];
    if (!domain) return false;
    
    // Verifica se o domínio está na lista de universidades cadastradas
    return universities.some(uni => uni.dominio === domain);
  };

  // Obter nome da universidade pelo domínio
  const getUniversityByEmail = (email) => {
    const domain = email.split('@')[1];
    return universities.find(uni => uni.dominio === domain);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    // Verificar conectividade primeiro
    if (connectionStatus === "error") {
      setError("❌ Sem conexão com o servidor. Tente novamente em alguns segundos.");
      return;
    }
    
    if (!isValidEmail(email)) {
      setError("Apenas emails com domínio edu são permitidos");
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        if (authError.message.includes('Invalid login credentials')) {
          setError("❌ Email ou senha incorretos");
        } else if (authError.message.includes('Email not confirmed')) {
          setError("❌ Verifique seu email antes de fazer login");
        } else if (authError.message.includes('Too many requests')) {
          setError("❌ Muitas tentativas. Aguarde um momento");
        } else {
          setError("❌ Erro no login: " + authError.message);
        }
        return;
      }

      // Verificar se o email foi confirmado
      if (!authData.user.email_confirmed_at) {
        setError("Por favor, verifique seu email antes de fazer login. Cheque sua caixa de entrada");
        await supabase.auth.signOut();
        return;
      }

      // Tentar buscar dados do perfil do usuário
      let userProfile = null;
      try {
        userProfile = await userService.getUserById(authData.user.id);
      } catch (profileError) {
        userProfile = null;
      }
      
      // Se encontrou perfil válido, usar ele
      if (userProfile && userProfile.nome && userProfile.nome !== 'Usuário') {
        onLogin({ ...authData.user, profileData: userProfile });
      } else {
        // Criar perfil básico com dados do auth e tentar salvar no banco
        const basicProfile = {
          id: authData.user.id,
          nome: authData.user.user_metadata?.nome || authData.user.email?.split('@')[0] || 'Usuário',
          email: authData.user.email,
          status: "solteiro(a)",
          cidade: "",
          quemSouEu: "Estudante",
          curso: "",
          periodo: "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Tentar criar o usuário no banco
        try {
          await userService.createUser(basicProfile);
        } catch (createError) {
          // Continuar mesmo se não conseguir criar no banco
        }
        
        onLogin({ ...authData.user, profileData: basicProfile });
      }
    } catch (err) {
      setError("Erro ao fazer login. Tente novamente");
    }
  };

  const handleRegisterRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidEmail(email)) {
      const domain = email.split('@')[1];
      setError(`Domínio "${domain}" não autorizado. Apenas e-mails de universidades cadastradas são permitidos.`);
      return;
    }

    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    if (!nome.trim()) {
      setError("Por favor, informe seu nome completo");
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            nome: nome, // Salva o nome nos metadados do usuário
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          setError("Este email já está cadastrado");
        } else {
          setError("Erro ao criar conta. Tente novamente");
        }
        return;
      }

      // Criar perfil inicial no database
      if (authData.user) {
        try {
          const university = getUniversityByEmail(email);
          const universityName = university ? `${university.nome} (${university.sigla})` : '';
          
          await userService.createUser({
            id: authData.user.id,
            nome: nome,
            email: email,
            universidade: universityName,
            status: "solteiro(a)",
            cidade: "",
            quemSouEu: "Estudante",
            curso: "",
            periodo: "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        } catch (createError) {
          // Não falhar o registro se não conseguir criar o perfil
        }

        showToast("Conta criada! Verifique seu email na caixa de entrada para ativar sua conta", "success");
        setMode("login");
        setEmail("");
        setPassword("");
        setNome("");
        setSuccess("");
      }
    } catch (err) {
      setError("Erro ao criar conta. Tente novamente");
    }
  };

  // Função para recuperar senha
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setResetEmailSent(false);

    if (!email) {
      setError("Por favor, informe seu email");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Por favor, use um email válido com domínio .edu");
      return;
    }

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        if (resetError.message.includes('rate limit')) {
          setError("Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.");
        } else {
          setError("Erro ao enviar email. Tente novamente.");
        }
        return;
      }

      setResetEmailSent(true);
      showToast("Email de recuperação enviado! Verifique sua caixa de entrada.", "success");
      setSuccess("✅ Email de recuperação enviado! Verifique sua caixa de entrada e spam.");
      
      // Voltar para login após 3 segundos
      setTimeout(() => {
        setMode("login");
        setEmail("");
        setSuccess("");
        setResetEmailSent(false);
      }, 5000);

    } catch (err) {
      setError("Erro ao processar solicitação. Tente novamente.");
    }
  };

  return (
    <div className="orkut-login-container">
      <div className="orkut-login-header">
        <h1 className="orkut-title">toNetwork</h1>
      </div>

      {mode === "login" && (
        <form className="orkut-login-form" onSubmit={handleLogin}>
          <h2>Login</h2>
          <input
            type="email"
            placeholder="Email (.edu.)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="orkut-btn-primary">Entrar</button>
          {error && <div className="orkut-error">{error}</div>}
          <div className="orkut-login-switch">
            Não tem conta? <button type="button" onClick={() => setMode("register")}>Criar conta</button>
          </div>
          <div className="orkut-login-switch" style={{ marginTop: '8px' }}>
            <button 
              type="button" 
              onClick={() => setMode("forgot-password")}
              style={{
                background: 'none',
                border: 'none',
                color: '#2d5aa0',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9em'
              }}
            >
              Esqueceu sua senha?
            </button>
          </div>
        </form>
      )}

      {mode === "forgot-password" && (
        <form className="orkut-login-form" onSubmit={handleForgotPassword}>
          <h2>Recuperar Senha</h2>
          <p style={{ 
            fontSize: '0.9em', 
            color: '#666', 
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            {!resetEmailSent ? 
              "Digite seu email e enviaremos um link para redefinir sua senha." :
              "Email enviado! Verifique sua caixa de entrada."
            }
          </p>
          
          {!resetEmailSent && (
            <>
              <input
                type="email"
                placeholder="Seu email (.edu.)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="orkut-btn-primary">
                Enviar Link de Recuperação
              </button>
            </>
          )}
          
          {error && <div className="orkut-error">{error}</div>}
          {success && <div className="orkut-success">{success}</div>}
          
          <div className="orkut-login-switch" style={{ marginTop: '16px' }}>
            <button 
              type="button" 
              onClick={() => {
                setMode("login");
                setEmail("");
                setError("");
                setSuccess("");
                setResetEmailSent(false);
              }}
            >
              ← Voltar para o login
            </button>
          </div>
        </form>
      )}

      {mode === "register" && (
        <form className="orkut-login-form" onSubmit={handleRegisterRequest}>
          <h2>Criar Conta</h2>
          <input
            type="text"
            placeholder="Nome completo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email (.edu.)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Senha (mínimo 6 caracteres)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          
          <button type="submit" className="orkut-btn-primary">Criar Conta</button>
          {error && <div className="orkut-error">{error}</div>}
          {success && <div className="orkut-success">{success}</div>}
          
          {/* Botão para Ver Universidades */}
          {universities.length > 0 && (
            <button 
              type="button"
              onClick={() => setShowUniversitiesList(true)}
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f0f4f9',
                border: '2px solid #d0dbe8',
                borderRadius: '8px',
                color: '#2d5aa0',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
                fontSize: '0.9em',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2d5aa0';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f0f4f9';
                e.target.style.color = '#2d5aa0';
              }}
            >
              Ver Universidades Cadastradas ({universities.length})
            </button>
          )}
```          
          <div className="orkut-login-switch">
            Já tem conta? <button type="button" onClick={() => setMode("login")}>Fazer login</button>
          </div>
        </form>
      )}

      {/* Toast Profissional */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Modal de Universidades */}
      {showUniversitiesList && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowUniversitiesList(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: '#2d5aa0',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '3px solid #1e3a6e'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.3em', fontWeight: '700' }}>
                Universidades Cadastradas
              </h3>
              <button
                onClick={() => setShowUniversitiesList(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              <p style={{ 
                margin: '0 0 16px 0', 
                color: '#666',
                fontSize: '0.95em',
                lineHeight: '1.5'
              }}>
                Apenas e-mails com os domínios abaixo podem criar conta:
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {universities.map((uni, index) => (
                  <div 
                    key={uni.id}
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      border: '2px solid #d0dbe8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2d5aa0';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 90, 160, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d0dbe8';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Logo/Cor */}
                    {uni.logo_url ? (
                      <img 
                        src={uni.logo_url} 
                        alt={uni.sigla}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          objectFit: 'contain',
                          background: 'white',
                          padding: '4px'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        background: uni.cor_tag || '#667eea',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '0.9em'
                      }}>
                        {uni.sigla.substring(0, 3)}
                      </div>
                    )}

                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '700',
                        fontSize: '1.1em',
                        color: '#333',
                        marginBottom: '4px'
                      }}>
                        {uni.nome}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.9em'
                      }}>
                        <span style={{
                          padding: '4px 10px',
                          background: uni.cor_tag || '#667eea',
                          color: 'white',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '0.85em'
                        }}>
                          {uni.sigla}
                        </span>
                        <span style={{
                          color: '#666',
                          fontFamily: 'monospace',
                          background: 'white',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid #e5eaf5'
                        }}>
                          @{uni.dominio}
                        </span>
                      </div>
                    </div>

                    {/* Número */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'white',
                      border: '2px solid ' + (uni.cor_tag || '#667eea'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      color: uni.cor_tag || '#667eea',
                      fontSize: '0.9em'
                    }}>
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>

              {universities.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#999'
                }}>
                  Nenhuma universidade cadastrada ainda.
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              background: '#f0f4f9',
              borderTop: '2px solid #d0dbe8',
              textAlign: 'center',
              fontSize: '0.85em',
              color: '#666'
            }}>
              Use seu e-mail institucional para criar sua conta
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;